import { el, svg } from "../core/dom";
import { createRequiredMarker } from "../core/required-marker";
import { productPurposeKey } from "../core/productMatrix";
import { part } from "../appearance/parts";
import type { SelectionControlSkin, Style } from "../appearance/types";
import { styles as classicStyles, type NoticeStyles } from "../styles/modal";
import {
  DEFAULT_HEADING_COLOR,
  DEFAULT_TEXT_COLOR,
} from "../appearance/constants";
import { DEFAULT_SELECTION_CONTROL } from "../constants";
import {
  hasRequiredDataElement,
  isBlockingRequiredElement,
} from "./consentSelection";
import type { PurposeItemData, SelectionControlType } from "../api/types";
import type { SelectionControlRefs } from "../types";
import { renderSelectionControl } from "./selection-control";

export type ModalPurposeRefs = {
  container: HTMLElement;
  /** Data element controls, keyed `${stateKey}-${elementUuid}`. */
  elementControls: Map<string, SelectionControlRefs>;
  purposeControls: Map<string, SelectionControlRefs>;
  collapseButtons: Map<string, HTMLElement>;
  dataElementContainers: Map<string, HTMLElement>;
  chevrons: Map<string, SVGElement>;
  titleElements: Map<string, HTMLElement>;
  descriptionElements: Map<string, HTMLElement>;
  dataElementLabels: Map<string, HTMLElement>;
};

/** How the purpose rows are drawn: the notice's resolved look. */
export type PurposeLook = {
  styles: NoticeStyles;
  responsive: Record<string, Style>;
  headingColor: string;
  textColor: string;
  /** Descriptions, when the admin set a muted colour; else `textColor`. */
  mutedTextColor?: string;
  selectionControl: SelectionControlType;
  /** The notice language, for the control's own copy. */
  language?: string;
  controlSkin?: SelectionControlSkin;
  /** Marks each row for the appearance's stylesheet (glass hairlines). */
  rowClassName?: string;
  /** A yes/no pair, a dropdown or a switch needs a roomier data element row. */
  roomyControls: boolean;
  drawsSwitches: boolean;
};

export const CLASSIC_PURPOSE_LOOK: PurposeLook = {
  styles: classicStyles,
  responsive: {},
  headingColor: DEFAULT_HEADING_COLOR,
  textColor: DEFAULT_TEXT_COLOR,
  selectionControl: DEFAULT_SELECTION_CONTROL,
  roomyControls: false,
  drawsSwitches: false,
};

const settledMark = (consented: boolean, label: string): HTMLElement =>
  consented
    ? createGreenCheckmark(16, label)
    : el("div", {
        role: "img",
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "16px",
          height: "16px",
          borderRadius: "50%",
          border: "2px solid #d0d5dd",
          backgroundColor: "transparent",
          flexShrink: 0,
          boxSizing: "border-box",
        },
        "aria-label": label,
      });

export function renderModalPurposes(
  purposes: PurposeItemData[],
  options: {
    selectedPurposes: Record<string, boolean>;
    collapsedPurposes: Record<string, boolean>;
    selectedDataElements: Record<string, boolean>;
    initialDataElementSelections?: Record<string, boolean>;
    recordedAnswers?: Record<string, boolean>;
    look?: PurposeLook;
    isAlreadyConsented: boolean;
    productUuid?: string;
    isRowLocked?: (purposeUuid: string) => boolean;
    /** Rows go straight into this element instead of a container of their own. */
    host?: HTMLElement;
    getTranslatedText: (key: string, defaultText: string, itemId?: string) => string;
    onPurposeToggle: (purposeUuid: string, productUuid?: string) => void;
    onPurposeCollapse: (purposeUuid: string, productUuid?: string) => void;
    onDataElementToggle: (elementUuid: string, purposeUuid: string, productUuid?: string) => void;
  }
): ModalPurposeRefs {
  const {
    selectedPurposes,
    collapsedPurposes,
    selectedDataElements,
    initialDataElementSelections = {},
    recordedAnswers = {},
    look = CLASSIC_PURPOSE_LOOK,
    isAlreadyConsented,
    productUuid,
    isRowLocked,
    getTranslatedText,
    onPurposeToggle,
    onPurposeCollapse,
    onDataElementToggle,
  } = options;
  const { styles, responsive, headingColor, textColor } = look;

  const elementControls = new Map<string, SelectionControlRefs>();
  const purposeControls = new Map<string, SelectionControlRefs>();
  const collapseButtons = new Map<string, HTMLElement>();
  const dataElementContainers = new Map<string, HTMLElement>();
  const chevrons = new Map<string, SVGElement>();
  const titleElements = new Map<string, HTMLElement>();
  const descriptionElements = new Map<string, HTMLElement>();
  const dataElementLabels = new Map<string, HTMLElement>();

  const container = options.host ?? el("div", { style: styles.optionsContainer });

  for (const purpose of purposes) {
    const purposeWrapper = el("div", { className: look.rowClassName ?? null });
    const stateKey = productPurposeKey(productUuid, purpose.uuid);
    const locked = isRowLocked ? isRowLocked(purpose.uuid) : isAlreadyConsented;
    const purposeName = getTranslatedText("purposes.name", purpose.name, purpose.uuid);

    const chevron = svg(
      "svg",
      {
        width: "7",
        height: "12",
        viewBox: "0 0 7 12",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        stroke: headingColor,
        "stroke-width": "2",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        style: {
          transform: !collapsedPurposes[stateKey] ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.3s ease",
        },
        "aria-hidden": "true",
      },
      svg("path", { d: "M1 1L6 6L1 11" })
    );
    chevrons.set(stateKey, chevron);

    const chevronContainer = el(
      "div",
      { style: { display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "5px" } },
      chevron
    );

    const titleEl = el("h2", {
      id: `purpose-name-${stateKey}`,
      ...part("purpose-title"),
      style: {
        ...styles.optionTitle,
        ...responsive.optionTitle,
        color: headingColor,
      },
    });
    // Appended after `textContent`, which replaces every existing child.
    titleEl.textContent = purposeName;
    if (hasRequiredDataElement(purpose)) {
      titleEl.appendChild(createRequiredMarker());
    }
    titleElements.set(stateKey, titleEl);

    const descEl = el("h3", {
      id: `purpose-description-${stateKey}`,
      style: {
        ...styles.optionDescription,
        ...responsive.optionDescription,
        color: look.mutedTextColor ?? textColor,
      },
    });
    descEl.textContent = getTranslatedText("purposes.description", purpose.description, purpose.uuid);
    descriptionElements.set(stateKey, descEl);

    const textContainer = el("div", { style: styles.optionTextContainer }, titleEl, descEl);

    const collapseBtn = el("div", {
      style: styles.optionLeft,
      role: "button",
      tabIndex: "0",
      "aria-expanded": String(!collapsedPurposes[stateKey]),
      "aria-controls": `purpose-${stateKey}`,
      "aria-label": `${collapsedPurposes[stateKey] ? "Expand" : "Collapse"} ${purposeName} details`,
      onClick: () => onPurposeCollapse(purpose.uuid, productUuid),
      onKeydown: (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPurposeCollapse(purpose.uuid, productUuid);
        }
      },
    }, chevronContainer, textContainer);
    collapseButtons.set(stateKey, collapseBtn);

    const purposeControl = renderSelectionControl({
      control: look.selectionControl,
      checked: selectedPurposes[stateKey] || false,
      onSelect: () => onPurposeToggle(purpose.uuid, productUuid),
      label: `Select all data elements for ${purposeName}`,
      groupName: `redacto-purpose-${stateKey}`,
      recordedAnswer: recordedAnswers[stateKey],
      size: "small",
      locked,
      lockedLabel: `Already consented to ${purposeName}`,
      language: look.language,
      skin: look.controlSkin,
    });
    if (!locked) {
      purposeControls.set(stateKey, purposeControl);
    }

    const optionRow = el(
      "div",
      { ...part("purpose"), style: styles.optionItem },
      collapseBtn,
      purposeControl.element
    );

    const deContainer = el("div", {
      id: `purpose-${stateKey}`,
      style: {
        ...styles.dataElementsContainer,
        ...(look.roomyControls ? styles.dataElementsContainerRoomy : {}),
      },
    });

    if (purpose.data_elements?.length > 0) {
      for (const dataElement of purpose.data_elements) {
        const combinedId = `${stateKey}-${dataElement.uuid}`;
        const elementName = getTranslatedText("data_elements.name", dataElement.name, dataElement.uuid);

        const labelSpan = el("span", {
          id: `data-element-${stateKey}-${dataElement.uuid}`,
          style: {
            ...styles.dataElementText,
            ...responsive.dataElementText,
            color: textColor,
          },
        });
        labelSpan.textContent = elementName;
        dataElementLabels.set(combinedId, labelSpan);

        if (isBlockingRequiredElement(dataElement)) {
          labelSpan.appendChild(createRequiredMarker());
        }

        let deRightElement: HTMLElement;
        if (!locked) {
          // The notice's own control, so a radio or dropdown notice asks the
          // data element the same way it asks the purpose. Kept a small round
          // check when purposes draw as switches.
          const control = renderSelectionControl({
            control: look.selectionControl,
            checked: selectedDataElements[combinedId] || false,
            onSelect: () => onDataElementToggle(dataElement.uuid, purpose.uuid, productUuid),
            label: `Select ${elementName}${isBlockingRequiredElement(dataElement) ? " (required)" : ""}`,
            groupName: `redacto-data-element-${combinedId}`,
            size: "small",
            className: "redacto-checkbox-small",
            language: look.language,
            skin: look.controlSkin,
          });
          elementControls.set(combinedId, control);
          deRightElement = control.element;
        } else {
          // A settled row records what was answered, yes or no, which the
          // control's locked state (always yes) cannot say.
          const wasSelected = initialDataElementSelections[combinedId] || false;
          deRightElement = settledMark(
            wasSelected,
            `${wasSelected ? "Consented" : "Not consented"} to ${elementName}`
          );
        }

        const deRow = el(
          "div",
          {
            ...part("data-element"),
            style: {
              ...styles.dataElementItem,
              ...(look.roomyControls ? styles.dataElementItemRoomy : {}),
            },
          },
          labelSpan,
          deRightElement
        );
        deContainer.appendChild(deRow);
      }
    }

    dataElementContainers.set(stateKey, deContainer);
    deContainer.style.display = !collapsedPurposes[stateKey] && purpose.data_elements?.length > 0 ? "flex" : "none";

    purposeWrapper.appendChild(optionRow);
    purposeWrapper.appendChild(deContainer);
    container.appendChild(purposeWrapper);
  }

  return {
    container,
    elementControls,
    purposeControls,
    collapseButtons,
    dataElementContainers,
    chevrons,
    titleElements,
    descriptionElements,
    dataElementLabels,
  };
}

export function createGreenCheckmark(size: number, ariaLabel: string): HTMLElement {
  const svgSize = size === 20 ? 12 : 10;
  const strokeWidth = size === 20 ? "2" : "1.5";
  const pathD = size === 20 ? "M4 6L5.5 7.5L8.5 4.5" : "M3 5L4.5 6.5L7.5 3.5";

  const checkSvg = svg(
    "svg",
    {
      width: String(svgSize),
      height: String(svgSize),
      viewBox: `0 0 ${svgSize} ${svgSize}`,
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "aria-hidden": "true",
    },
    svg("path", {
      d: pathD,
      stroke: "white",
      "stroke-width": strokeWidth,
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
    })
  );

  return el("div", {
    role: "img",
    style: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: "50%",
      backgroundColor: "#10B981",
      flexShrink: 0,
    },
    "aria-label": ariaLabel,
  }, checkSvg);
}
